import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  keyframes,
  Button,
  Grid,
  Paper,
  Divider,
  Link,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Backdrop,
  Radio,
  RadioGroup,
  FormControl,
  Menu,

  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { useAuthContext } from '../context/AuthContext';
import { processinput } from '../queries/processinput';
import { submitcase } from '../queries/submitcase';
import { CancerType, UserType, CaseStatus } from '../schema';
import { getCaseUntilGenerated } from '../queries/getCaseUntilGenerated';
import { retryAsync } from '../utils/retry';
import { confirmTermsOfService } from '../queries/confirmtermsofservice';
import { submitsurvey } from '../queries/submitsurvey';
import { usePersistentNavigate } from '../utils/navigation';
import { FileUpload } from '../submitcase';
import ChatBot from "react-chatbotify";
import { sendmessage } from '../queries/sendmessage';
import { voteAIRecommendation } from '../queries/voteAIRecommendation';
import InsufficientDataModal from './InsufficientDataModal';


// Define keyframes for animations
const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const CaseReport: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get('case_id');
  const isProductionDeployment = searchParams.get('isProductionDeployment') === 'true';
  const isAdminView = searchParams.get('from') === 'admin';
  const reportContentRef = useRef<HTMLDivElement>(null);
  const submitted = useRef(false);
  const [displayText, setDisplayText] = useState("Loading");
  const [loadingStage, setLoadingStage] = useState(1); // 0: parsing, 1: thinking/loading, 2: display - start at 1 to show loading UI immediately
  const [isProcessing, setIsProcessing] = useState(false); // true when running AI processing (Thinking)
  const [reportContent, setReportContent] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { caseContextText, caseContextJson, caseInputType, contextCancerType, caseContextUploadedFiles, contextReasoning, contextVerbosity, clearCaseContext } = useCaseIntake();

  const { accessToken, setTermsOfServiceAccepted, termsOfServiceAccepted, completedRegistration, setAccessToken, logout, userType, isLoading } = useAuthContext();

  // Redirect unauthenticated users to landing page
  useEffect(() => {
    if (!isLoading && !accessToken) {
      navigate('/');
    }
  }, [isLoading, accessToken, navigate]);

  // Top-right menu state & handlers
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleMenuHome = () => {
    handleMenuClose();
    navigate('/');
  };
  const handleMenuLogout = async () => {
    handleMenuClose();
    await logout();
  };

  // States for the disclosure popup
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [disclosureChecked, setDisclosureChecked] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);

  // Add this state with your other state declarations 
  const [logMessages, setLogMessages] = useState<string[]>([]);

  // Add these states with other states
  const [impactRating, setImpactRating] = useState<number | null>(null);
  const [surveySubmitted, setSurveySubmitted] = useState(false);

  // Add this state with your other states (near existing impactRating state)
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [existingSurveyResponse, setExistingSurveyResponse] = useState<number | null>(null);
  const [existingSurveyComment, setExistingSurveyComment] = useState<string | null>(null);


  // Add this state
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [submitCaseResponse, setSubmitCaseResponse] = useState<any>(null);

  // AI Recommendation Selection
  const [recommendations, setRecommendations] = useState<any[]>([]);

  // State for insufficient data modal and retry flow
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [missingFactors, setMissingFactors] = useState<string[]>([]);
  const [originalInputForRetry, setOriginalInputForRetry] = useState('');
  const [isRetry, setIsRetry] = useState(false);
  const [isRetrySubmitting, setIsRetrySubmitting] = useState(false);
  const [selectedRecommendationIndex, setSelectedRecommendationIndex] = useState<number>(0);
  const [voting, setVoting] = useState(false);
  const [votedIndex, setVotedIndex] = useState<number | null>(null);
  const [voteSelection, setVoteSelection] = useState<number | null>(null);

  // Report type toggle (concise vs detailed)
  const [reportType, setReportType] = useState<'concise' | 'detailed'>('concise');

  // Track adjudication IDs from the case response
  const [aiAdjudicationId, setAiAdjudicationId] = useState<string | null>(null);
  const [humanAdjudicationId, setHumanAdjudicationId] = useState<string | null>(null);

  // Provider input text (for admin view)
  const [providerInput, setProviderInput] = useState<string | null>(null);
  const [providerInputExpanded, setProviderInputExpanded] = useState<boolean>(false);

  // Dialog states for vote confirmation
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [voteDialogMessage, setVoteDialogMessage] = useState('');

  const handleCloseVoteDialog = () => {
    setVoteDialogOpen(false);
  };

  // Case status and failure modal
  const [caseStatus, setCaseStatus] = useState<CaseStatus>(CaseStatus.INVALID_CASE_STATUS);
  const [showFailureModal, setShowFailureModal] = useState(false);

  // Handle disclosure visibility
  const showDisclosureIfApplicable = () => {
    if (termsOfServiceAccepted) {
      setIsReturningUser(true);
    } else {
      setIsReturningUser(false);
    }
    setShowDisclosure(true);
  };

  // Replace the runProcessingLog function with this version

  const runProcessingLog = () => {
    // ensure display text shows "Thinking" when running the processing log
    setDisplayText("Thinking");
    setIsProcessing(true);
    // Start with initial placeholder messages - real streaming will add more
    setLogMessages(["Parsing Provider Input..."]);

    // Add placeholder messages while waiting for streaming to start
    setTimeout(() => {
      setLogMessages(prev => {
        if (!prev.includes("Scanning for PHI...")) {
          return [...prev, "Scanning for PHI..."];
        }
        return prev;
      });
    }, 4000);

    setTimeout(() => {
      setLogMessages(prev => {
        if (!prev.includes("Evaluating Input Quality...")) {
          return [...prev, "Evaluating Input Quality..."];
        }
        return prev;
      });
    }, 8000);

    setTimeout(() => {
      setLogMessages(prev => {
        if (!prev.includes("Researching Background Context...")) {
          return [...prev, "Researching Background Context..."];
        }
        return prev;
      });
    }, 16000);
  };

  // Submit case and process the response
  const submitCaseAsync = async () => {
    // We're actively running the AI processing pipeline -> "Thinking"
    setIsProcessing(true);
    setDisplayText("Thinking");
    setLoadingStage(1);
    // Reset isRetry for fresh submissions from landing page
    setIsRetry(false);
    runProcessingLog(); // Start the processing log

    if (!accessToken) {
      navigate('/login');
      return;
    }

    const isAdmin = userType === UserType.ADMIN;

    if (!completedRegistration && !isAdmin) {
      navigate('/complete-registration');
      return;
    }

    try {
      if (caseInputType === CaseInputType.TEXTAREA) {
        setLoadingStage(0);

        const processInputResponse = await retryAsync(() => processinput({
          textArea: caseContextText,
          fileBytes: [],
          fileNames: [],
          isRetry: false,  // Fresh submissions are never retries
        }, accessToken || '', setAccessToken, logout));

        if (processInputResponse.containsPii) {
          navigate('/', {
            state: {
              rejectedText: caseContextText,
              toastMessage: "PrecisCa AI detected PII in your input, which is prohibited in our Terms of Service. Please try again with anonymized input."
            }
          });
          return;
        }

        if (processInputResponse.invalidInput) {
          navigate('/', {
            state: {
              rejectedText: caseContextText,
              toastMessage: "PrecisCa AI is only available for licensed medical professionals seeking second opinions on cancer cases. Please re-submit a request with a valid cancer case."
            }
          });
          return;
        }

        if (processInputResponse.insufficientData) {
          if (isRetry) {
            // Final rejection on retry - go home with rejection message
            navigate('/', {
              state: {
                toastMessage: "Insufficient input provided. Please collect more information about the details of this case and re-submit later."
              }
            });
            return;
          } else {
            // First attempt - show modal for augmentation
            console.log('=== INSUFFICIENT DATA DEBUG ===');
            console.log('Full processInputResponse:', JSON.stringify(processInputResponse, null, 2));
            console.log('insufficientData:', processInputResponse.insufficientData);
            console.log('missingFactors:', processInputResponse.missingFactors);
            console.log('missingFactors length:', processInputResponse.missingFactors?.length);
            console.log('caseJson:', processInputResponse.caseJson);
            console.log('containsPii:', processInputResponse.containsPii);
            console.log('invalidInput:', processInputResponse.invalidInput);
            console.log('=== END DEBUG ===');
            setIsProcessing(false);
            setOriginalInputForRetry(caseContextText);
            setMissingFactors(processInputResponse.missingFactors || []);
            setShowInsufficientModal(true);
            return;
          }
        }

        if (!processInputResponse.caseJson) {
          navigate('/', {
            state: {
              rejectedText: caseContextText,
              toastMessage: "Unable to process your request at this time. Please try again."
            }
          });
          return;
        }

        setLoadingStage(1);

        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: processInputResponse.caseJson,
          cancerType: CancerType.NOT_PROVIDED,
          files: [],
          reasoning: contextReasoning,
          verbosity: contextVerbosity,
          textAreaContext: caseContextText,
          isCreatedByDeployment: isProductionDeployment,
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout, setLogMessages);

        handleCaseRecommendations(c);

        // Update URL with case_id to retain it on refresh
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('case_id', submitCaseResponse.caseId);
          return newParams;
        }, { replace: true });

        showDisclosureIfApplicable();
        setLoadingStage(2);
        setIsProcessing(false);

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
      else if (caseInputType === CaseInputType.FORM) {
        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: caseContextJson,
          cancerType: contextCancerType,
          files: [],
          reasoning: contextReasoning,
          verbosity: contextVerbosity,
          isCreatedByDeployment: isProductionDeployment,
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        console.log('Submit case response:', submitCaseResponse);
        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout, setLogMessages);

        handleCaseRecommendations(c);

        // Update URL with case_id to retain it on refresh
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('case_id', submitCaseResponse.caseId);
          return newParams;
        }, { replace: true });

        showDisclosureIfApplicable();
        setLoadingStage(2);
        setIsProcessing(false);

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
      else if (caseInputType === CaseInputType.FILE) {
        setLoadingStage(0); // Parsing input

        const fileBytes: Uint8Array[] = [];
        const fileNames: string[] = [];
        const filesToSubmit: FileUpload[] = [];
        for (const uploadedFile of (caseContextUploadedFiles || [])) {
          const file = uploadedFile.file;
          const buffer = await file.arrayBuffer();

          fileBytes.push(new Uint8Array(buffer));
          fileNames.push(file.name);

          const bytes = new Uint8Array(buffer);

          filesToSubmit.push({
            filename: file.name,
            content: bytes
          });
        }

        const processInputResponse = await retryAsync(() => processinput({
          textArea: "",
          fileBytes: fileBytes,
          fileNames: fileNames,
          isRetry: false,  // File uploads don't support text augmentation via modal
        }, accessToken || '', setAccessToken, logout));

        // Check for PII
        if (processInputResponse.containsPii) {
          navigate('/', {
            state: {
              toastMessage: "PrecisCa AI detected PII in your input, which is prohibited in our Terms of Service. Please try again with anonymized input."
            }
          });
          return;
        }

        // Check for invalid input (not a cancer case)
        if (processInputResponse.invalidInput) {
          navigate('/', {
            state: {
              toastMessage: "PrecisCa AI is only available for licensed medical professionals seeking second opinions on cancer cases. Please re-submit a request with a valid cancer case."
            }
          });
          return;
        }

        // For file uploads, we can't show the augmentation modal with text editing
        // So we reject immediately with a helpful message
        if (processInputResponse.insufficientData) {
          const missingList = processInputResponse.missingFactors?.length > 0
            ? ` Missing data points include: ${processInputResponse.missingFactors.slice(0, 4).join(', ')}.`
            : '';
          navigate('/', {
            state: {
              toastMessage: `The provided files do not contain enough information to form a clinical opinion. Please provide additional documents or use the text input option.${missingList}`
            }
          });
          return;
        }

        if (!processInputResponse.caseJson) {
          navigate('/', {
            state: {
              toastMessage: "Unable to process your request at this time. Please try again."
            }
          });
          return;
        }

        setLoadingStage(1); // Thinking

        const submitCaseResponse = await retryAsync(() => submitcase({
          caseJson: processInputResponse.caseJson,
          cancerType: CancerType.NOT_PROVIDED,
          files: filesToSubmit,
          reasoning: contextReasoning,
          verbosity: contextVerbosity,
          textAreaContext: "",
          isCreatedByDeployment: isProductionDeployment,
        }, accessToken || '', setAccessToken, logout));

        // Store response for later use
        setSubmitCaseResponse(submitCaseResponse);

        console.log('Submit case response:', submitCaseResponse);
        const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout, setLogMessages);

        handleCaseRecommendations(c);

        // Update URL with case_id to retain it on refresh
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('case_id', submitCaseResponse.caseId);
          return newParams;
        }, { replace: true });

        showDisclosureIfApplicable();
        setLoadingStage(2);
        setIsProcessing(false);

        sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
      }
    } catch (error) {
      console.error("Failed to process case after multiple retries:", error);

      const navState: any = {
        toastMessage: "Unable to process your request at this time. Please try again."
      };

      if (caseInputType === CaseInputType.TEXTAREA) {
        navState.rejectedText = caseContextText;
      }

      navigate('/', { state: navState });
    }
  };

  // Handle accepting the disclosure
  const handleAcceptDisclosure = async () => {
    if (!isReturningUser) {
      await confirmTermsOfService(accessToken || '', setAccessToken, logout);
      setTermsOfServiceAccepted(true);
    }
    setShowDisclosure(false);
  };

  // Check if all checkboxes are checked
  const allChecked = privacyChecked && disclosureChecked && agreementChecked;

  // Submit the case when the component loads
  useEffect(() => {
    window.scrollTo(0, 0);

    if (!accessToken) return;

    if (caseId) {
      // We're loading an existing case by id (not re-running AI). show "Loading" instead of "Thinking".
      setIsProcessing(false);
      setDisplayText("Loading");
      setLoadingStage(1);

      getCaseUntilGenerated(caseId, accessToken, setAccessToken, logout).then((c) => {
        // Set submitCaseResponse to enable survey, chat, and voting features
        setSubmitCaseResponse({
          caseId: caseId,
          aiAdjudicationId: c.aiAdjudicationId,
          humanAdjudicationId: c.humanAdjudicationId,
        });
        handleCaseRecommendations(c);
        if (userType !== UserType.ADMIN && userType !== UserType.REVIEWER) {
          showDisclosureIfApplicable();
        }
        setLoadingStage(2);
        setIsProcessing(false);
      }).catch(error => {
        console.error("Failed to get case after multiple retries:", error);
        setReportContent('# Error\nFailed to retrieve case report. Please try again later.');
        setLoadingStage(2);
        setIsProcessing(false);
      });
      return;
    }

    if (sessionStorage.getItem('lastSubmittedCaseResponse')) {
      const savedResponse = JSON.parse(sessionStorage.getItem('lastSubmittedCaseResponse') || '{}');
      submitted.current = true;
      setSubmitCaseResponse(savedResponse);
      // loading previously-submitted case -> "Loading"
      setIsProcessing(false);
      setDisplayText("Loading");
      setLoadingStage(1);
      getCaseUntilGenerated(savedResponse.caseId, accessToken, setAccessToken, logout).then((c) => {
        handleCaseRecommendations(c);
        setLoadingStage(2);
        setIsProcessing(false);
      }).catch(() => setIsProcessing(false));

      return;
    }

    if (!submitted.current) {
      submitted.current = true;
      submitCaseAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, caseId]);

  // Cleanup: wipe case_id and from parameters when unmounting
  useEffect(() => {
    return () => {
      // Only run cleanup if we are actually leaving the /case-report route
      // This prevents it from running on double-mount in dev mode (mount -> unmount -> mount)
      if (window.location.pathname !== '/case-report') {
        clearCaseContext();
      }
    };
  }, [clearCaseContext]);

  // Group recommendations by report_type and source model
  const groupedRecommendations = useMemo(() => {
    const groups: { [key: string]: { detailed?: any; concise?: any } } = {};

    recommendations.forEach(rec => {
      // Use source_model + source_model_version + review_generated as the key to group related recommendations
      // This ensures reviewed versions appear as separate options from original versions
      const reviewSuffix = rec.reviewGenerated ? '-reviewed' : '';
      const key = `${rec.sourceModel}-${rec.sourceModelVersion}${reviewSuffix}`;
      if (!groups[key]) {
        groups[key] = {};
      }

      // Default to 'detailed' if reportType is not present (backward compatibility)
      const recType = rec.reportType || 'detailed';

      if (recType === 'detailed') {
        groups[key].detailed = rec;
      } else if (recType === 'concise') {
        groups[key].concise = rec;
      }
    });

    return groups;
  }, [recommendations]);

  // Check if we have both concise and detailed versions available
  const hasBothVersions = useMemo(() => {
    return Object.values(groupedRecommendations).some(
      group => group.detailed && group.concise
    );
  }, [groupedRecommendations]);

  // Helper function to find the group index from a recommendation ID
  const findGroupIndexByRecommendationId = useCallback((recId: string): number => {
    const groupKeys = Object.keys(groupedRecommendations);
    for (let i = 0; i < groupKeys.length; i++) {
      const group = groupedRecommendations[groupKeys[i]];
      if (group.detailed?.id === recId || group.concise?.id === recId) {
        return i;
      }
    }
    return -1;
  }, [groupedRecommendations]);

  // Update selected index when recommendations or groupedRecommendations change
  useEffect(() => {
    if (recommendations.length > 0 && Object.keys(groupedRecommendations).length > 0) {
      const groupKeys = Object.keys(groupedRecommendations);

      // First priority: Find a reviewed version (reviewGenerated = true)
      const reviewedGroupIndex = groupKeys.findIndex(key => {
        const group = groupedRecommendations[key];
        const rec = group.detailed || group.concise;
        return rec?.reviewGenerated === true;
      });

      if (reviewedGroupIndex >= 0) {
        setSelectedRecommendationIndex(reviewedGroupIndex);
        return;
      }

      // Second priority: Try to find the adjudicated recommendation using stored IDs or submitCaseResponse
      const adjId = humanAdjudicationId || aiAdjudicationId ||
        submitCaseResponse?.humanAdjudicationId ||
        submitCaseResponse?.aiAdjudicationId;

      if (adjId) {
        const adjudicatedRec = recommendations.find(r => r.id === adjId);

        if (adjudicatedRec) {
          const groupIndex = findGroupIndexByRecommendationId(adjudicatedRec.id);
          if (groupIndex >= 0) {
            setSelectedRecommendationIndex(groupIndex);
          }
        }
      }
    }
  }, [recommendations, groupedRecommendations, submitCaseResponse, findGroupIndexByRecommendationId, aiAdjudicationId, humanAdjudicationId]);

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!reportContentRef.current) return;

    try {
      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('case-report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Update the handleSurveySubmit function
  const handleSurveySubmit = async () => {
    if (impactRating !== null && submitCaseResponse?.caseId) {
      try {
        setSubmittingSurvey(true);

        await submitsurvey({
          caseId: submitCaseResponse.caseId,
          rating: impactRating,
          comment: feedbackText || undefined // Only include if non-empty
        }, accessToken || '', setAccessToken, logout);

        console.log('Survey submitted with rating:', impactRating, 'and comment:', feedbackText);
        setSurveySubmitted(true);
      } catch (error) {
        console.error('Error submitting survey:', error);
      } finally {
        setSubmittingSurvey(false);
      }
    }
  };

  // Track if welcome message has been shown to prevent duplicates
  const hasWelcomed = useRef(false);

  // Reset welcome state when case changes
  useEffect(() => {
    hasWelcomed.current = false;
  }, [caseId]);

  const settings = useMemo(() => ({
    general: { embedded: false },
    chatHistory: { disabled: true },
    header: {
      title: "PrecisCa AI",
      avatar: "/insignia.jpg",
    },
    bot: {
      avatar: "/insignia.jpg",
    },
    notification: {
      disabled: true,
    },
    tooltip: {
      mode: "CLOSE",
      text: "Ask a follow-up question",
    },
    chatButton: {
      icon: "/insignia.jpg"
    },
    chatInput: {
      botDelay: 1000,
    },
    fileAttachment: {
      disabled: true,
    },
    emoji: {
      disabled: true,
    },
    footer: {
      text: ""
    }
  }), []);

  const styles = useMemo(() => ({
    tooltipStyle: {
      fontSize: '16px',
      backgroundColor: '#4299e1',
      color: 'white',
    },
    headerStyle: {
      background: '#2c5282',
      color: 'white',
    },
    userBubbleStyle: {
      backgroundColor: '#4299e1',
      color: 'white',
    },
    botBubbleStyle: {
      backgroundColor: '#f1f5f9',
      color: '#2d3748',
    },
    sendButtonStyle: {
      backgroundColor: '#4299e1',
      color: 'white',
    },
    chatInputAreaStyle: {
      minHeight: '60px',
    }
  }), []);

  // ... (inside component)

  const handleSendMessage = useCallback(async (params: any) => {
    const userInput = params.userInput;
    let currentCaseId = caseId;

    if (!currentCaseId && submitCaseResponse?.caseId) {
      currentCaseId = submitCaseResponse.caseId;
    }

    if (!currentCaseId) {
      return "Error: Case ID not found. Please try reloading the page.";
    }

    try {
      const response = await sendmessage({
        caseId: currentCaseId,
        chatHistory: "", // We might need to manage history if the API expects it, or if ChatBot manages it we might need to format it. 
        // For now, passing empty as per request "Pass in the context of the conversation (i.e. chat_history in SendMessageRequest)..."
        // Wait, the user said "Pass in the context of the conversation... along with case data... and the most recent message".
        // If the API is stateless regarding history, we need to send it. 
        // react-chatbotify might provide history. 
        // Let's check params.
        incomingMessage: userInput
      }, accessToken || '', setAccessToken, logout);

      return response.response;
    } catch (error) {
      console.error("Error sending message:", error);
      return "Sorry, I encountered an error processing your message.";
    }
  }, [caseId, submitCaseResponse, accessToken, setAccessToken, logout]);

  // Helper function to handle setting recommendations and adjudication IDs
  const handleCaseRecommendations = useCallback((c: any) => {
    setCaseStatus(c.status);
    if (c.status === CaseStatus.GENERATION_FAILED) {
      setShowFailureModal(true);
    }

    if (c.recommendations && c.recommendations.length > 0) {
      setRecommendations(c.recommendations);

      // Store adjudication IDs - the useEffect will handle setting the correct grouped index
      setAiAdjudicationId(c.aiAdjudicationId || null);
      setHumanAdjudicationId(c.humanAdjudicationId || null);

      // Store provider input if available (for admin view)
      if (c.providerInput) {
        setProviderInput(c.providerInput);
      }

      // Set vote selection if human adjudication exists
      if (c.humanAdjudicationId) {
        const humanAdjIndex = c.recommendations.findIndex((r: any) => r.id === c.humanAdjudicationId);
        if (humanAdjIndex >= 0) {
          setVoteSelection(humanAdjIndex);
          setVotedIndex(humanAdjIndex);
        }
      } else {
        setVoteSelection(null);
      }
    } else {
      setReportContent(c.markdown);
      // Store provider input if available even if no recommendations
      if (c.providerInput) {
        setProviderInput(c.providerInput);
      }
    }


    // Store existing survey data if present
    setExistingSurveyResponse(c.surveyResponse || null);
    setExistingSurveyComment(c.surveyComment || null);
    if (c.surveyResponse) {
      setSurveySubmitted(true);
    }
  }, []);

  // Handler for insufficient data modal submission (retry with augmented input)
  const handleInsufficientDataSubmit = useCallback(async (augmentedInput: string) => {
    setIsRetrySubmitting(true);
    setShowInsufficientModal(false);
    setIsRetry(true);
    setIsProcessing(true);
    setDisplayText("Thinking");
    setLoadingStage(0);
    setLogMessages([]);
    runProcessingLog();

    try {
      const processInputResponse = await retryAsync(() => processinput({
        textArea: augmentedInput,
        fileBytes: [],
        fileNames: [],
        isRetry: true,
      }, accessToken || '', setAccessToken, logout));

      if (processInputResponse.containsPii) {
        navigate('/', {
          state: {
            rejectedText: augmentedInput,
            toastMessage: "PrecisCa AI detected PII in your input, which is prohibited in our Terms of Service. Please try again with anonymized input."
          }
        });
        return;
      }

      if (processInputResponse.invalidInput) {
        navigate('/', {
          state: {
            rejectedText: augmentedInput,
            toastMessage: "PrecisCa AI is only available for licensed medical professionals seeking second opinions on cancer cases. Please re-submit a request with a valid cancer case."
          }
        });
        return;
      }

      if (processInputResponse.insufficientData) {
        // Final rejection on retry
        navigate('/', {
          state: {
            toastMessage: "Insufficient input provided. Please collect more information about the details of this case and re-submit later."
          }
        });
        return;
      }

      if (!processInputResponse.caseJson) {
        navigate('/', {
          state: {
            rejectedText: augmentedInput,
            toastMessage: "Unable to process your request at this time. Please try again."
          }
        });
        return;
      }

      setLoadingStage(1);

      const submitCaseResponse = await retryAsync(() => submitcase({
        caseJson: processInputResponse.caseJson,
        cancerType: CancerType.NOT_PROVIDED,
        files: [],
        reasoning: contextReasoning,
        verbosity: contextVerbosity,
        textAreaContext: augmentedInput,
      }, accessToken || '', setAccessToken, logout));

      setSubmitCaseResponse(submitCaseResponse);

      const c = await getCaseUntilGenerated(submitCaseResponse.caseId, accessToken || '', setAccessToken, logout, setLogMessages);

      handleCaseRecommendations(c);

      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('case_id', submitCaseResponse.caseId);
        return newParams;
      }, { replace: true });

      showDisclosureIfApplicable();
      setLoadingStage(2);
      setIsProcessing(false);

      sessionStorage.setItem('lastSubmittedCaseResponse', JSON.stringify(submitCaseResponse));
    } catch (error) {
      console.error('Error during retry submission:', error);
      navigate('/', {
        state: {
          toastMessage: "An error occurred while processing your request. Please try again."
        }
      });
    } finally {
      setIsRetrySubmitting(false);
      setIsRetry(false);
    }
  }, [accessToken, setAccessToken, logout, navigate, contextReasoning, contextVerbosity, retryAsync, handleCaseRecommendations, setSearchParams, runProcessingLog, showDisclosureIfApplicable]);

  const flow = useMemo(() => ({
    start: {
      message: () => {
        if (hasWelcomed.current) {
          // Return void/undefined to indicate no message should be shown if supported, 
          // or an empty string if that's safer. The type error said "string | void" is expected.
          return;
        }
        hasWelcomed.current = true;
        return "Feel free to ask any follow-up questions about this case.";
      },
      path: "loop"
    },
    loop: {
      message: handleSendMessage,
      path: "loop"
    }
  }), [handleSendMessage]);


  // Get the formatted provider input (formatted as JSON if applicable)
  const formattedProviderInput = useMemo(() => {
    if (!providerInput) return null;
    try {
      // Attempt to parse and re-stringify with indentation if it's JSON
      const parsed = JSON.parse(providerInput);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If not JSON, return as is
      return providerInput;
    }
  }, [providerInput]);

  // Get the current recommendation to display based on selected index and report type
  const currentRecommendation = useMemo(() => {
    const groupKeys = Object.keys(groupedRecommendations);
    if (selectedRecommendationIndex >= groupKeys.length) return null;

    const currentGroup = groupedRecommendations[groupKeys[selectedRecommendationIndex]];

    // Try to get the preferred report type, fall back to the other if not available
    if (reportType === 'concise') {
      return currentGroup.concise || currentGroup.detailed;
    } else {
      return currentGroup.detailed || currentGroup.concise;
    }
  }, [groupedRecommendations, selectedRecommendationIndex, reportType]);

  // Update report content when current recommendation changes
  useEffect(() => {
    if (currentRecommendation) {
      setReportContent(currentRecommendation.markdown);
    }
  }, [currentRecommendation]);

  const handleRecommendationChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedRecommendationIndex(newValue);
  };

  const submitVote = async (selectionIndex: number) => {
    if (!submitCaseResponse?.caseId && !caseId) return;
    const cid = submitCaseResponse?.caseId || caseId;

    // Get the actual recommendation ID from the selected index using grouped recommendations
    const groupKeys = Object.keys(groupedRecommendations);
    if (selectionIndex >= groupKeys.length) {
      console.error("Invalid selection index", selectionIndex);
      return;
    }

    const currentGroup = groupedRecommendations[groupKeys[selectionIndex]];
    // Always vote for the detailed version (it's the primary recommendation)
    const recommendationToVote = currentGroup.detailed || currentGroup.concise;

    if (!recommendationToVote || !recommendationToVote.id) {
      console.error("No recommendation ID found for index", selectionIndex);
      return;
    }

    setVoting(true);
    try {
      await voteAIRecommendation(cid, recommendationToVote.id, accessToken || '', setAccessToken, logout);
      setVotedIndex(selectionIndex);
      setVoteDialogMessage(`You have selected Option ${selectionIndex + 1} as the best option`);
      setVoteDialogOpen(true);
    } catch (error) {
      console.error("Error voting:", error);
      setVoteDialogMessage("Failed to submit vote. Please try again.");
      setVoteDialogOpen(true);
    } finally {
      setVoting(false);
    }
  };

  const handleVoteSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelection = Number(event.target.value);
    setVoteSelection(newSelection);
    await submitVote(newSelection);
  };

  return (
    <>
      {/* Insufficient Data Modal for input augmentation */}
      <InsufficientDataModal
        open={showInsufficientModal}
        missingFactors={missingFactors}
        originalInput={originalInputForRetry}
        onSubmit={handleInsufficientDataSubmit}
        onClose={() => {
          setShowInsufficientModal(false);
          clearCaseContext();
          navigate('/', { replace: true });
        }}
        isSubmitting={isRetrySubmitting}
      />
      {/* Top Navigation Bar */}
      <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
        <Toolbar sx={{
          minHeight: { xs: '56px', sm: '64px' },
          justifyContent: 'space-between',
          padding: { xs: '0 16px', sm: '0 24px' }
        }}>
          {/* left spacer (branding removed) */}
          {/* Logo on the left — uses a public file at /logo.png (place your logo in frontend/public/logo.png) */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="a"
              href="/"
              sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <Box
                component="img"
                src="/logo.jpg"
                alt="Precisca"
                sx={{
                  height: { xs: 28, sm: 36 },
                  width: 'auto',
                  display: 'block',
                  mr: 1
                }}
              />
            </Box>
          </Box>


          {/* spacer to keep menu button on the right even when logo is hidden */}
          <Box sx={{ flex: 1 }} />

          <IconButton
            color="inherit"
            sx={{ color: '#2c5282' }}
            aria-controls={menuOpen ? 'top-right-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleMenuOpen}
          >
            <MenuIcon />
          </IconButton>

          {/* Menu for Home / Log Out */}
          <Menu
            id="top-right-menu"
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleMenuHome}>Home</MenuItem>
            {userType === UserType.PROVIDER && (
              <MenuItem onClick={() => { handleMenuClose(); navigate('/my-cases'); }}>My Cases</MenuItem>
            )}
            <MenuItem onClick={handleMenuLogout}>Log Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Dialog
        open={showDisclosure}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        hideBackdrop
        sx={{
          zIndex: theme.zIndex.modal + 1,  // Ensure dialog is above backdrop
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 3 },  // Add margin on mobile to prevent edge-to-edge
            maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100% - 64px)' },  // Ensure dialog fits on screen
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            pt: 2,
            color: '#2c5282',
            fontWeight: 600,
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}
        >
          Important Disclosure
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500, color: '#2d3748' }}>
            <br />This proprietary material does not constitute medical or health advice to a patient but rather is intended to provide an educational perspective. PrecisCa does not engage in the practice of medicine. Please review our{' '}
            <Link
              href="/legal/PrecisCa.AI Privacy Policy.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Privacy Policy
            </Link>,{' '}
            <Link
              href="/legal/PrecisCa.AI Disclaimer.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Disclaimer
            </Link>, and{' '}
            <Link
              href="/legal/PrecisCa.AI Terms and Conditions.pdf"
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#4299e1', fontWeight: 500 }}
            >
              Terms of Service
            </Link>{' '}
            to learn more.
          </Typography>

          {/* Only show checkboxes for first-time users */}
          {!isReturningUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyChecked}
                    onChange={(e) => setPrivacyChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the Privacy Policy.
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={disclosureChecked}
                    onChange={(e) => setDisclosureChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the Disclosure Statement.
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    sx={{
                      color: '#a0aec0',
                      '&.Mui-checked': { color: '#4299e1' }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: '#2d3748' }}>
                    I have read and agree to the User Agreement.
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleAcceptDisclosure}
            disabled={!isReturningUser && !allChecked}
            fullWidth
            sx={{
              bgcolor: (isReturningUser || allChecked) ? '#4299e1' : '#cbd5e0',
              color: 'white',
              py: { xs: 1.5, sm: 1.2 },  // Larger padding on mobile for better touch target
              minHeight: { xs: '48px', sm: '44px' },  // Ensure minimum touch target size
              fontSize: { xs: '0.95rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: (isReturningUser || allChecked) ? '0 2px 5px rgba(66, 153, 225, 0.3)' : 'none',
              opacity: (isReturningUser || allChecked) ? 1 : 0.7,
              touchAction: 'manipulation',  // Improve touch responsiveness
              WebkitTapHighlightColor: 'transparent',  // Remove tap highlight on iOS
              '&:hover': (isReturningUser || allChecked) ? {
                bgcolor: '#2c5282',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)'
              } : {},
              '&:active': {
                transform: 'scale(0.98)'  // Visual feedback on touch
              }
            }}
          >
            {isReturningUser ? "Continue to Case Report" : "Accept to See Case Report"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop for blurring when disclosure is shown */}
      <Backdrop
        sx={{
          zIndex: theme.zIndex.drawer - 1,
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(255, 255, 255, 0.6)'
        }}
        open={showDisclosure}
      />

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: loadingStage < 2 ? 'center' : 'flex-start',
          minHeight: 'calc(100vh - 64px)',
          padding: { xs: 2, sm: 3 },
          mb: 8,
          overflowY: 'auto',
          filter: showDisclosure ? 'blur(2px)' : 'none',
          transition: 'filter 0.3s ease'
        }}
      >
        {(isProcessing || loadingStage === 1) ? (
          <>
            {/* Processing Log */}
            {isProcessing && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 60, sm: 70 }, // Increased to move below gray bar
                  left: { xs: 2, sm: 10 }, // Match container padding
                  padding: 2,
                  maxWidth: { xs: '85%', sm: '50%' },
                  textAlign: 'left',
                  zIndex: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '8px',
                  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.00)'
                }}
              >
                {(() => {
                  // On mobile (xs), limit to last 8 messages to prevent overlap with spinner
                  const isMobile = window.innerWidth < 600;
                  const displayedMessages = isMobile && logMessages.length > 10
                    ? logMessages.slice(-10)
                    : logMessages;

                  return displayedMessages.map((message, displayIndex) => {
                    // PrecisCa branded blues - random 50/50 based on message hash
                    const blueColors = ['#25a0d7', '#2f5166']; // Light blue, Dark blue
                    const hash = message.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    const colorIndex = hash % 2;
                    return (
                      <Typography
                        key={displayIndex}
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          color: blueColors[colorIndex],
                          mb: 0.5,
                          opacity: 0.9,
                          fontWeight: 500,
                          whiteSpace: 'nowrap' // Prevent text wrapping
                        }}
                      >
                        {message}
                      </Typography>
                    );
                  });
                })()}
              </Box>
            )}

            {/* Loading animation */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 200,
                height: 200,
                mt: { xs: 4, sm: 0 } // Move slightly lower on mobile
              }}
            >
              {/* SVG Circle Animation */}
              <Box sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                animation: `${rotate} 2s linear infinite`
              }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                    stroke="#4299e1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeDasharray="283"
                    strokeDashoffset="283"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="283"
                      to="0"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              </Box>

              {/* Pulsing Text */}
              <Typography
                variant="h4"
                sx={{
                  color: '#2c5282',
                  fontWeight: '500',
                  animation: `${pulse} 2s ease-in-out infinite`,
                  fontSize: { xs: '1.1rem', sm: '1.5rem' },
                  transition: 'opacity 0.5s ease',
                  minWidth: '140px',
                  textAlign: 'center',
                  whiteSpace: 'pre-line'
                }}
              >
                {displayText}
              </Typography>
            </Box>

            {/* Processing time message */}
            {isProcessing && (
              <Typography
                variant="body2"
                sx={{
                  color: '#718096',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mt: 5,
                  textAlign: 'center'
                }}
              >
                Case processing may take up to 3 minutes. <br />Thank you for your patience.
              </Typography>
            )}
          </>
        ) : (
          <Box sx={{ width: '100%' }}>
            {/* Mobile download button */}
            <Box sx={{
              display: { xs: 'flex', sm: 'none' },
              justifyContent: 'center',
              mb: 3
            }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  color: '#2c5282',
                  borderColor: '#2c5282',
                  py: 1,
                  px: 2,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  '&:hover': {
                    borderColor: '#4299e1',
                    color: '#4299e1',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                Download PDF
              </Button>
            </Box>

            {/* Provider Input Section - only show for admins when available */}
            {isAdminView && providerInput && (
              <Box sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setProviderInputExpanded(!providerInputExpanded)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Provider Input
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {providerInputExpanded ? 'Click to collapse' : 'Click to expand'}
                    </Typography>
                  </Box>
                  {providerInputExpanded && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          p: 1,
                          maxHeight: '400px',
                          overflow: 'auto'
                        }}
                      >
                        {formattedProviderInput}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Recommendation Selector - Updated to use grouped recommendations */}
            {isAdminView && Object.keys(groupedRecommendations).length > 0 && (
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Paper sx={{ flex: 1 }}>
                  <Tabs
                    value={selectedRecommendationIndex}
                    onChange={handleRecommendationChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {Object.keys(groupedRecommendations).map((key, index) => {
                      const group = groupedRecommendations[key];
                      const rec = group.detailed || group.concise;
                      const isReviewed = rec?.reviewGenerated || key.endsWith('-reviewed');
                      const label = isReviewed ? `Option ${index + 1} (Reviewed)` : `Option ${index + 1}`;
                      return <Tab key={index} label={label} value={index} />;
                    })}
                  </Tabs>
                </Paper>
              </Box>
            )}

            {/* Report Type Tabs - only show if both versions exist */}
            {hasBothVersions && (
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Paper sx={{ flex: 1 }}>
                  <Tabs
                    value={reportType}
                    onChange={(event: React.SyntheticEvent, newValue: 'concise' | 'detailed') => {
                      setReportType(newValue);
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Concise" value="concise" />
                    <Tab label="Detailed" value="detailed" />
                  </Tabs>
                </Paper>
              </Box>
            )}


            {/* Markdown content with styling */}
            <Paper
              elevation={0}
              ref={reportContentRef}
              sx={{
                p: { xs: 2, sm: 4 },
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                mb: 3
              }}
            >
              <Box sx={{
                color: '#4a5568',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '1rem',
                lineHeight: 1.7,
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: '#2c5282',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  mb: 2,
                  mt: 3
                },
                '& h1': {
                  fontSize: { xs: '1.7rem', sm: '2.1rem' },
                  borderBottom: '1px solid #e2e8f0',
                  pb: 1
                },
                '& h2': {
                  fontSize: { xs: '1.4rem', sm: '1.8rem' }
                },
                '& h3': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                '& p': {
                  mb: 2
                },
                '& a': {
                  color: '#4299e1',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                },
                '& ul, & ol': {
                  paddingLeft: 3,
                  mb: 2
                },
                '& li': {
                  mb: 1
                },
                '& blockquote': {
                  borderLeft: '4px solid #e2e8f0',
                  pl: 2,
                  fontStyle: 'italic',
                  my: 2
                },
                '& code': {
                  fontFamily: 'monospace',
                  backgroundColor: '#f7fafc',
                  padding: '2px 4px',
                  borderRadius: 1
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto'
                },
                '& hr': {
                  border: 0,
                  height: '1px',
                  backgroundColor: '#e2e8f0',
                  my: 3
                }
              }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({ node, ...props }) => (
                      <Box sx={{ overflowX: 'auto', my: 3 }}>
                        <table
                          style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            border: '1px solid #e2e8f0'
                          }}
                          {...props}
                        />
                      </Box>
                    ),
                    thead: ({ node, ...props }) => (
                      <thead style={{ backgroundColor: '#f7fafc' }} {...props} />
                    ),
                    tbody: ({ node, ...props }) => (
                      <tbody {...props} />
                    ),
                    tr: ({ node, ...props }) => (
                      <tr
                        style={{
                          borderBottom: '1px solid #e2e8f0',
                        }}
                        {...props}
                      />
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderRight: '1px solid #e2e8f0',
                          color: '#2c5282'
                        }}
                        {...props}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        style={{
                          padding: '12px 16px',
                          borderRight: '1px solid #e2e8f0'
                        }}
                        {...props}
                      />
                    )
                  }}
                >
                  {reportContent}
                </ReactMarkdown>
              </Box>
            </Paper>

            {/* Voting Section */}
            {isAdminView && loadingStage === 2 && recommendations.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: '#2c5282',
                    fontWeight: 500,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  Which output option did you find the most helpful?
                </Typography>

                <FormControl component="fieldset">
                  <RadioGroup
                    value={voteSelection}
                    onChange={handleVoteSelection}
                  >
                    {Object.keys(groupedRecommendations).map((key, index) => (
                      <FormControlLabel
                        key={index}
                        value={index}
                        control={<Radio />}
                        label={`Option ${index + 1}`}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Paper>
            )}

            {/* Admin/Reviewer Survey View */}
            {loadingStage === 2 && (userType === UserType.ADMIN || userType === UserType.REVIEWER) && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: '#2c5282',
                    fontWeight: 500,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  How much will this opinion impact your ultimate treatment plan?
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ color: '#4a5568', fontWeight: 500 }}>
                    {existingSurveyResponse ? (
                      <>Rating: {existingSurveyResponse} ({
                        existingSurveyResponse === 1 ? "No impact" :
                          existingSurveyResponse === 2 ? "Minimal impact" :
                            existingSurveyResponse === 3 ? "Moderate impact" :
                              existingSurveyResponse === 4 ? "Significant impact" :
                                "Major impact"
                      })</>
                    ) : (
                      "No rating provided."
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body1" sx={{ color: '#4a5568' }}>
                    {existingSurveyComment ? (
                      <strong>Feedback: {existingSurveyComment}</strong>
                    ) : (
                      "No feedback provided."
                    )}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Survey Question (Providers only) */}
            {loadingStage === 2 && !surveySubmitted && userType === UserType.PROVIDER && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Box sx={{ width: '100%', px: { xs: 1, sm: 3 } }}>
                  {/* Rating buttons with numbers */}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: '#2c5282',
                      fontWeight: 500,
                      fontSize: { xs: '1rem', sm: '1.1rem' }
                    }}
                  >
                    How much will this opinion impact your ultimate treatment plan?
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Box
                        key={value}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '20%'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            mb: 0.5,
                            color: impactRating === value ? '#4299e1' : '#718096'
                          }}
                        >
                          {value}
                        </Typography>
                        <Radio
                          checked={impactRating === value}
                          onChange={() => setImpactRating(value)}
                          value={value}
                          name="rating-radio-buttons"
                          sx={{
                            color: '#a0aec0',
                            '&.Mui-checked': { color: '#4299e1' }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* Desktop labels */}
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      justifyContent: 'space-between',
                      width: '100%',
                      mt: 1
                    }}
                  >
                    {[
                      "No impact",
                      "Minimal impact",
                      "Moderate impact",
                      "Significant impact",
                      "Major impact"
                    ].map((label, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: impactRating === index + 1 ? 500 : 400,
                          color: impactRating === index + 1 ? '#4299e1' : '#718096',
                          width: '20%',
                          textAlign: 'center'
                        }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  {/* Mobile labels (only 1, 3, 5) */}
                  <Box
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      width: '100%',
                      mt: 1
                    }}
                  >
                    {/* Container that holds all 5 positions but only shows labels for 1, 3, 5 */}
                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 1 ? 500 : 400,
                          color: impactRating === 1 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        No{'\n'}impact
                      </Typography>
                    </Box>

                    {/* Empty space for position 2 */}
                    <Box sx={{ width: '20%' }}></Box>

                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 3 ? 500 : 400,
                          color: impactRating === 3 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        Moderate{'\n'}impact
                      </Typography>
                    </Box>

                    {/* Empty space for position 4 */}
                    <Box sx={{ width: '20%' }}></Box>

                    <Box sx={{ width: '20%', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: impactRating === 5 ? 500 : 400,
                          color: impactRating === 5 ? '#4299e1' : '#718096',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.2
                        }}
                      >
                        Major{'\n'}impact
                      </Typography>
                    </Box>
                  </Box>

                  {/* Optional Feedback Section */}
                  <Box sx={{ mt: 4, width: '100%', px: { xs: 0, sm: 0 } }}> {/* Removed any potential extra padding */}
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: '#2c5282',
                        fontWeight: 500,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      }}
                    >
                      Would you like to leave optional feedback on the quality of the recommendation?
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Optional feedback"
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#e2e8f0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#cbd5e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4299e1',
                          },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          fontSize: '0.85rem',
                          opacity: 0.7
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    disabled={impactRating === null || submittingSurvey}
                    onClick={handleSurveySubmit}
                    sx={{
                      bgcolor: impactRating !== null ? '#4299e1' : '#cbd5e0',
                      color: 'white',
                      py: 1,
                      px: 4,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      fontWeight: 500,
                      borderRadius: '8px',
                      boxShadow: impactRating !== null ? '0 2px 5px rgba(66, 153, 225, 0.3)' : 'none',
                      opacity: impactRating !== null ? 1 : 0.7,
                      '&:hover': impactRating !== null ? {
                        bgcolor: '#2c5282',
                      } : {}
                    }}
                  >
                    {submittingSurvey ? 'Submitting...' : 'Submit Survey'}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Thank you message after survey submission (Providers only) */}
            {loadingStage === 2 && surveySubmitted && userType === UserType.PROVIDER && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: '#f0f9ff'
                }}
              >
                <Typography
                  variant="body1"
                  align="center"
                  sx={{
                    color: '#2c5282',
                    fontWeight: 500
                  }}
                >
                  Thank you for your feedback!
                </Typography>
              </Paper>
            )}

            {/* Bottom buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 0, sm: 0 },
              gap: { xs: 3, sm: 4 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              {/* Desktop: Download PDF button */}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  color: '#2c5282',
                  borderColor: '#2c5282',
                  py: { xs: 1.2, sm: 1.5 },
                  px: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 500,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  width: { xs: '100%', sm: 'auto' },
                  transition: 'all 0.2s ease',
                  display: { xs: 'none', sm: 'flex' },
                  '&:hover': {
                    borderColor: '#4299e1',
                    color: '#4299e1',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                Download PDF
              </Button>

              {isAdminView ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    const params = new URLSearchParams();
                    const tab = searchParams.get('tab');
                    const page = searchParams.get('page');
                    const rowsPerPage = searchParams.get('rowsPerPage');
                    const userType = searchParams.get('userType');
                    if (tab) params.set('tab', tab);
                    if (page) params.set('page', page);
                    if (rowsPerPage) params.set('rowsPerPage', rowsPerPage);
                    if (userType) params.set('userType', userType);
                    if (searchParams.get('stagingEnv')) params.set('stagingEnv', searchParams.get('stagingEnv')!);

                    navigate({
                      pathname: '/admin',
                      search: params.toString()
                    });
                  }}
                  sx={{
                    bgcolor: '#4299e1',
                    color: 'white',
                    py: { xs: 1.2, sm: 1.5 },
                    px: { xs: 3, sm: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500,
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
                    width: { xs: '100%', sm: 'auto' },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#2c5282',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)',
                    },
                  }}
                >
                  Return to Admin Portal
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                  sx={{
                    bgcolor: '#4299e1',
                    color: 'white',
                    py: { xs: 1.2, sm: 1.5 },
                    px: { xs: 3, sm: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 500,
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
                    width: { xs: '100%', sm: 'auto' },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#2c5282',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)',
                    },
                  }}
                >
                  Submit Another Case
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#f1f5f9', py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 5 }}>
            {/* Logo Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                component="img"
                src="/logo no background.png"
                alt="Precisca"
                sx={{
                  height: 32,
                  width: 'auto',
                  mb: 2,
                  display: 'block'
                }}
              />
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Elevating cancer care through <br></br> virtual second opinions.
              </Typography>
            </Grid>

            {/* Products Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Products
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                AI Reviews
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Doctor Opinions
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Video Library
              </Link>
              <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Terms of Service
              </Link>
            </Grid>

            {/* Company Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Company
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                About
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Our Faculty
              </Link>
              <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Privacy Policy
              </Link>
              <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Disclaimer
              </Link>
            </Grid>

            {/* Newsletter Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Stay Updated
              </Typography>
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Stay abreast of the latest PrecisCa developments and new products.
              </Typography>
            </Grid>
          </Grid>

          {/* Copyright Section */}
          <Box sx={{ mt: { xs: 4, sm: 6 }, pt: { xs: 2, sm: 3 }, borderTop: '1px solid #e2e8f0' }}>
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'center', sm: 'center' }}
              spacing={{ xs: 2, sm: 0 }}
            >
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{
                    color: '#4a5568',
                    textAlign: { xs: 'center', sm: 'left' },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    © {new Date().getFullYear()} PrecisCa. All rights reserved.
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Box sx={{
                  display: 'flex',
                  gap: { xs: 2, sm: 3 },
                  justifyContent: { xs: 'center', sm: 'flex-end' }
                }}>
                  <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Disclaimer
                  </Link>
                  <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Terms of Use
                  </Link>
                  <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Privacy Policy
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      {loadingStage === 2 && !showDisclosure && <ChatBot settings={settings} flow={flow} styles={styles} />}

      {/* Vote Confirmation Dialog */}
      <Dialog
        open={voteDialogOpen}
        onClose={handleCloseVoteDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            pt: 2,
            color: '#2c5282',
            fontWeight: 600,
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}
        >
          Vote Submitted
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <DialogContentText sx={{ pt: 2, mb: 1, fontWeight: 500, color: '#2d3748', fontSize: '1rem' }}>
            {voteDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleCloseVoteDialog}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4299e1',
              color: 'white',
              py: 1.2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
              '&:hover': {
                bgcolor: '#2c5282',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog >

      {/* Generation Failure Dialog */}
      < Dialog
        open={showFailureModal}
        onClose={() => setShowFailureModal(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            pt: 2,
            color: '#c53030', // Red color for error
            fontWeight: 600,
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}
        >
          Generation Incomplete
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <DialogContentText sx={{ pt: 2, mb: 1, fontWeight: 500, color: '#2d3748', fontSize: '1rem' }}>
            This case did not complete the full generation process properly. However, we have displayed the available information collected so far, including any partial report data.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setShowFailureModal(false)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4299e1',
              color: 'white',
              py: 1.2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
              '&:hover': {
                bgcolor: '#2c5282',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)'
              }
            }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog >

    </>
  );
};

export default CaseReport;
